import { useState, useMemo, useCallback } from "react";
import { Box, Button, FormControl, FormLabel, Typography } from "@mui/material";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { CategorySelect } from "./CategorySelect";
import { Loading } from "../../../components/common/Loading";
import { useCategoriesQuery } from "../../../services/queryHooks";
import ColorDisplayAndPicker from "../../../components/common/ColorDisplayAndPicker";

export function CategoryDisplay({
	transaction,
	selectedType,
	setSelectedCategory,
	setSelectedSubcategory,
	selectedCategory,
	selectedSubcategory,
	isInModal,
	isError,
}) {
	const theme = useTheme();
	const { t } = useTranslation();
	const { data: allCategories, isPending: allCategoriesPending } =
		useCategoriesQuery();
	const categories = allCategories.filter(
		(category) =>
			category.type === (isInModal ? selectedType : transaction?.type)
	);

	const [anchorCategorySelect, setAnchorCategorySelect] = useState(null);

	const openCategorySelect = (event) => {
		event.stopPropagation();
		setAnchorCategorySelect(event.currentTarget);
	};

	const getCategoryData = useCallback(
		(categoryId, subcategoryId) => {
			const category = categories.find((c) => c._id === categoryId);
			const subcategory = category?.subcategories.find(
				(s) => s._id === subcategoryId
			);

			return {
				name:
					category?.groupName ??
					(category?.groupTranslationKey
						? t(category.groupTranslationKey)
						: t("noCategory")),
				color: category?.color || theme.palette.categories.default,
				subcategoryName:
					subcategory?.name ??
					(subcategory?.translationKey
						? t(subcategory.translationKey)
						: t("noSubcategory")),
				subcategoryEmoji: subcategory?.emoji || null,
			};
		},
		[categories, t, theme]
	);

	const activeCategoryData = useMemo(() => {
		const isSelectedDifferent =
			selectedCategory && selectedCategory !== transaction?.categoryId;

		const categoryId = isSelectedDifferent
			? selectedCategory
			: transaction?.categoryId;

		const subcategoryId = isSelectedDifferent
			? selectedSubcategory
			: transaction?.subcategoryId;

		return getCategoryData(categoryId, subcategoryId);
	}, [transaction, selectedCategory, selectedSubcategory, getCategoryData]);

	const outlineBoxStyle = {
		mt: isInModal ? 2 : 0,
		border: isInModal ? "1px solid" : "none",
		borderColor: isError ? theme.palette.error.main : "divider",
		borderRadius: "10px",
		p: 0,
		width: "100%",
		position: "relative",
	};

	const outlineLableStyle = {
		zIndex: 1,
		position: "absolute",
		top: -10,
		left: 12,
		borderRadius: "10px",
		backgroundColor: theme.palette.background.paper,
		px: 1,
		fontSize: 14,
		color: "text.secondary",
	};

	const categoryButtonStyles = {
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignContent: "center",
		alignItems: "start",
		lineHeight: 1.2,
		py: isInModal ? 2 : 1,
		px: 2,
		m: 0,
		height: "100%",
		width: "100%",
		opacity: !transaction || transaction?.includeInAnalysis ? 1 : 0.3,
		fontWeight: "normal",
		cursor: "pointer",
	};

	if (allCategoriesPending) {
		return <Loading />;
	}

	return (
		<Box
			sx={{
				display: "flex",
				alignContent: "center",
			}}
		>
			<FormControl
				variant="outlined"
				sx={outlineBoxStyle}
				onClick={(e) => openCategorySelect(e)}
			>
				{isInModal && (
					<FormLabel component="legend" sx={outlineLableStyle}>
						{t("category")}
					</FormLabel>
				)}

				<Button sx={categoryButtonStyles}>
					{activeCategoryData && (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
							}}
						>
							{selectedCategory && (
								<ColorDisplayAndPicker
									color={activeCategoryData.color}
									onColorChange={() => {}}
									isEditable={false}
								/>
							)}
							{activeCategoryData.name ||
								t(activeCategoryData.name)}
							<ChevronRight />

							{selectedSubcategory && (
								<Typography
									sx={{
										fontSize: "1rem",
										marginRight: "5px",
									}}
								>
									{activeCategoryData.subcategoryEmoji}
								</Typography>
							)}
							{activeCategoryData.subcategoryName ||
								t(activeCategoryData.subcategoryName)}
						</Box>
					)}
				</Button>
			</FormControl>

			<CategorySelect
				transaction={transaction}
				categories={categories}
				selectedType={selectedType}
				setSelectedCategory={setSelectedCategory}
				setSelectedSubcategory={setSelectedSubcategory}
				selectedCategory={selectedCategory}
				selectedSubcategory={selectedSubcategory}
				anchorCategorySelect={anchorCategorySelect}
				setAnchorCategorySelect={setAnchorCategorySelect}
				isInModal={isInModal}
			/>
		</Box>
	);
}
